import type { Relations } from "@inrev/common";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import {
	type AssistantSession,
	useAssistantSession,
} from "../../admin/shared/tools/assistant/useAssistantSession";
import type { AssistantConversationSelection } from "../../admin/shared/tools/assistant/useStoredAssistantConversation";

export type VerniOverlayView = "chat" | "history";

type VerniOverlayValue = AssistantSession & {
	view: VerniOverlayView;
	setView: (view: VerniOverlayView) => void;
};

/** Which Verni conversation the account overlay shows, keyed per account. */
export const useVerniOverlayState = (relation: Relations.Notes): VerniOverlayValue => {
	const assistantSession = useAssistantSession(relation);
	const [view, setView] = useState<VerniOverlayView>("chat");

	const { openConversation: openSessionConversation } = assistantSession;
	const openConversation = useCallback(
		(next: AssistantConversationSelection) => {
			openSessionConversation(next);
			setView("chat");
		},
		[openSessionConversation],
	);

	return useMemo(
		() => ({ ...assistantSession, view, setView, openConversation }),
		[assistantSession, view, openConversation],
	);
};

const VerniOverlayContext = createContext<VerniOverlayValue | null>(null);

export const VerniOverlayProvider = ({
	value,
	children,
}: {
	value: VerniOverlayValue;
	children: ReactNode;
}) => <VerniOverlayContext.Provider value={value}>{children}</VerniOverlayContext.Provider>;

export const useVerniOverlay = () => {
	const context = useContext(VerniOverlayContext);
	if (!context) throw new Error("useVerniOverlay must be used inside VerniOverlayProvider");
	return context;
};
